import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  User, LogOut, Menu, X, Home, BookOpen, GraduationCap,
  PlayCircle, Crown, Sparkles, Moon, Sun, BarChart3, Users
} from 'lucide-react';
import logoImage from '../../assets/images/logo/logo.png';

const Navbar: React.FC = () => {
  const { isAuthenticated, logout, currentUser } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isNightMode, setIsNightMode] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const location = useLocation();

  const isCurrentPath = (path: string) => location.pathname === path;

  const scrollToCourses = () => {
    const coursesSection = document.getElementById('courses-section');
    if (coursesSection) {
      coursesSection.scrollIntoView({ behavior: 'smooth' });
    } else if (location.pathname !== '/') {
      window.location.href = '/#courses-section';
    }
  };

  const toggleNightMode = () => {
    const newNightMode = !isNightMode;
    setIsNightMode(newNightMode);

    if (newNightMode) {
      document.body.classList.add('night-mode');
    } else {
      document.body.classList.remove('night-mode');
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = document.documentElement;
      const totalScrollable = Math.max(scrollHeight - clientHeight, 1);
      const progress = Math.min(Math.max((scrollTop / totalScrollable) * 100, 0), 100);
      setScrollProgress(progress);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinkClasses = (path: string) => `
    relative px-2 sm:px-3 lg:px-4 py-1.5 sm:py-2 lg:py-2.5 text-xs sm:text-sm font-semibold rounded-lg transition-all duration-500 group overflow-hidden
    ${isCurrentPath(path)
      ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-lg shadow-orange-500/25'
      : 'text-gray-700 hover:text-orange-700 hover:bg-gradient-to-r hover:from-orange-50 hover:to-orange-100'
    }
  `;

  return (
    <>
      <nav
        className="app-navbar fixed top-4 left-1/2 z-50 h-20 w-[280px] -translate-x-1/2 transform rounded-[50px] border-4 border-orange-600 bg-white/95 shadow-lg shadow-orange-200/20 backdrop-blur-2xl transition-all duration-300 sm:left-4 sm:right-4 sm:h-auto sm:w-auto sm:translate-x-0 sm:transform-none sm:rounded-3xl"
        dir="rtl"
      >
        <div className="relative mx-auto h-full max-w-full overflow-visible rounded-[inherit] px-2 sm:px-2 lg:px-4">
          <div className="relative flex h-full items-center py-1.5 sm:py-3 lg:py-4">
            {/* Scroll progress */}
            <div
              className={`absolute h-1 transition-all duration-300 ${scrollProgress > 0 ? 'translate-y-0 opacity-100' : '-translate-y-1 opacity-0'}`}
              style={{ bottom: '2px', left: '50px', right: '50px' }}
            >
              <div className="h-full w-full overflow-hidden rounded-full bg-orange-200/60">
                <div
                  className={`h-full transition-[width] duration-200 ${isNightMode ? 'bg-orange-400' : 'bg-gradient-to-r from-orange-500 via-amber-400 to-orange-600'}`}
                  style={{ width: `${scrollProgress}%` }}
                />
              </div>
            </div>

            {/* Desktop */}
            <div className="hidden w-full grid-cols-3 items-center lg:grid">
              <div className="flex items-center justify-start space-x-2 space-x-reverse">
                <Link to="/" className={navLinkClasses('/')}>
                  <Home className="ml-1.5 inline-block h-3 w-3 sm:h-4 sm:w-4" />
                  الرئيسية
                </Link>
                <button type="button" onClick={scrollToCourses} className={`${navLinkClasses('/courses')} cursor-pointer border-0 bg-transparent`}>
                  <BookOpen className="ml-1.5 inline-block h-3 w-3 sm:h-4 sm:w-4" />
                  الباقات
                </button>
                {isAuthenticated && (
                  <>
                    <Link to="/my-lectures" className={navLinkClasses('/my-lectures')}>
                      <PlayCircle className="ml-1.5 inline-block h-3 w-3 sm:h-4 sm:w-4" />
                      محاضراتي
                    </Link>
                    <Link to="/enrolled-courses" className={navLinkClasses('/enrolled-courses')}>
                      <GraduationCap className="ml-1.5 inline-block h-3 w-3 sm:h-4 sm:w-4" />
                      باقاتي
                    </Link>
                  </>
                )}
              </div>

              <Link to="/" className="group mx-4 flex min-w-0 max-w-full items-center justify-center">
                <div className="relative">
                  <div className="absolute inset-0 rounded-full bg-gradient-to-r from-orange-400 to-orange-500 opacity-30 blur-lg transition-opacity duration-500 group-hover:opacity-50" />
                  <div className="relative h-12 w-[8.25rem] overflow-hidden rounded-2xl border-2 border-orange-600 bg-gradient-to-br from-white to-orange-50 shadow-lg transition-all duration-500 sm:h-14 sm:w-[9.5rem] lg:h-[4.25rem] lg:w-[11rem] group-hover:scale-105 group-hover:shadow-xl">
                    <div className="flex h-full w-full items-center justify-center p-1.5 sm:p-2 lg:p-2.5">
                      <img
                        src={logoImage}
                        alt="منصة مستر وليد المصري"
                        className="max-h-full max-w-full object-contain object-center select-none"
                      />
                    </div>
                  </div>
                  <div className="absolute -right-2 -top-2 rounded-full bg-gradient-to-r from-orange-400 to-orange-500 p-1.5 shadow-lg animate-pulse">
                    <Crown className="h-3 w-3 text-white" />
                  </div>
                </div>
              </Link>

              <div className="flex items-center justify-end gap-3">
                <div className="flex items-center space-x-2 space-x-reverse">
                  <Link to="/parent-monitoring" className={navLinkClasses('/parent-monitoring')}>
                    <Users className="ml-1.5 inline-block h-3 w-3 sm:h-4 sm:w-4" />
                    متابعة ولي الأمر
                  </Link>
                  {isAuthenticated && (
                    <Link to="/statistics" className={navLinkClasses('/statistics')}>
                      <BarChart3 className="ml-1.5 inline-block h-3 w-3 sm:h-4 sm:w-4" />
                      احصائياتي
                    </Link>
                  )}
                </div>

                <button type="button" onClick={toggleNightMode} className="group relative p-1 focus:outline-none" aria-label={isNightMode ? 'الوضع النهاري' : 'الوضع الليلي'}>
                  <div
                    className={`relative h-8 w-16 rounded-full shadow-2xl transition-all duration-700 ease-out group-hover:scale-105 group-hover:shadow-xl ${
                      isNightMode
                        ? 'bg-gradient-to-br from-orange-600 via-orange-600 to-orange-700 shadow-orange-600/40'
                        : 'bg-gradient-to-br from-orange-100 via-orange-200 to-orange-300 shadow-orange-600/20'
                    }`}
                  >
                    {isNightMode && (
                      <div className="absolute inset-0 overflow-hidden rounded-full">
                        <div className="absolute left-2 top-1 h-1 w-1 animate-pulse rounded-full bg-yellow-200" />
                        <div className="absolute right-3 top-2 h-0.5 w-0.5 animate-ping rounded-full bg-white" />
                        <div className="absolute bottom-2 left-3 h-0.5 w-0.5 animate-pulse rounded-full bg-yellow-100" style={{ animationDelay: '1s' }} />
                      </div>
                    )}
                    <div
                      className={`absolute top-1 h-6 w-6 rounded-full shadow-lg transition-all duration-700 ease-out ${
                        isNightMode ? 'left-9' : 'left-1'
                      } ${
                        isNightMode
                          ? 'bg-gradient-to-br from-orange-600 via-orange-600 to-orange-700 shadow-orange-600/50'
                          : 'bg-gradient-to-br from-orange-600 via-orange-600 to-orange-600 shadow-orange-600/30'
                      } group-hover:shadow-xl`}
                    >
                      <div
                        className={`absolute inset-0 rounded-full transition-all duration-700 ${
                          isNightMode ? 'ring-2 ring-orange-600/50 ring-offset-1 ring-offset-transparent' : 'ring-1 ring-orange-600/50'
                        }`}
                      />
                      <div className="relative z-10 flex h-full w-full items-center justify-center">
                        {isNightMode ? (
                          <Sun className="h-3.5 w-3.5 text-white drop-shadow-sm transition-all duration-500 group-hover:rotate-180 group-hover:scale-110" />
                        ) : (
                          <Moon className="h-3.5 w-3.5 text-white drop-shadow-sm transition-all duration-500 group-hover:rotate-12 group-hover:scale-110" />
                        )}
                      </div>
                      <div
                        className={`absolute inset-0.5 rounded-full blur-sm transition-all duration-700 ${
                          isNightMode ? 'bg-gradient-to-br from-orange-600/30 to-orange-700/30' : 'bg-gradient-to-br from-orange-600/50 to-orange-600/30'
                        }`}
                      />
                    </div>
                    <div className="absolute inset-y-0 left-2 flex items-center">
                      <div className={`h-1 w-1 rounded-full transition-all duration-500 ${!isNightMode ? 'bg-orange-600' : 'bg-transparent'}`} />
                    </div>
                    <div className="absolute inset-y-0 right-2 flex items-center">
                      <div className={`h-1 w-1 rounded-full transition-all duration-500 ${isNightMode ? 'bg-orange-600' : 'bg-transparent'}`} />
                    </div>
                    <div
                      className={`absolute inset-0 rounded-full transition-all duration-700 ${
                        isNightMode
                          ? 'shadow-[0_0_20px_rgba(249,115,22,0.3)] group-hover:shadow-[0_0_30px_rgba(249,115,22,0.4)]'
                          : 'shadow-[0_0_10px_rgba(249,115,22,0.2)] group-hover:shadow-[0_0_15px_rgba(249,115,22,0.3)]'
                      }`}
                    />
                  </div>
                </button>

                {isAuthenticated ? (
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2 rounded-lg border border-orange-200/50 bg-gradient-to-r from-orange-50 to-orange-100 px-3 py-1.5 shadow-lg shadow-orange-500/10">
                      <div className="flex h-6 w-6 items-center justify-center rounded-full bg-gradient-to-br from-orange-400 to-orange-500 shadow-md">
                        <User className="h-3 w-3 text-white" />
                      </div>
                      <span className="bg-gradient-to-r from-orange-600 to-orange-700 bg-clip-text text-xs font-bold text-transparent sm:text-sm">
                        {currentUser?.name}
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={logout}
                      className="rounded-lg bg-gradient-to-r from-red-400 to-pink-500 px-3 py-1.5 text-sm font-semibold text-white shadow-lg transition-all duration-300 hover:shadow-xl hover:shadow-red-400/25"
                    >
                      <LogOut className="mr-1.5 inline-block h-3 w-3" />
                      تسجيل الخروج
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    <Link
                      to="/login"
                      className="rounded-lg px-3 py-1.5 text-xs font-semibold text-gray-700 transition-all hover:bg-gradient-to-r hover:from-orange-50 hover:to-orange-100 hover:text-orange-700 sm:text-sm"
                    >
                      تسجيل الدخول
                    </Link>
                    <Link
                      to="/register"
                      className="rounded-lg bg-gradient-to-r from-orange-500 to-orange-600 px-4 py-1.5 text-xs font-semibold text-white shadow-lg transition-all hover:shadow-xl hover:shadow-orange-500/25 sm:text-sm"
                    >
                      <Sparkles className="mr-1 inline-block h-3 w-3" />
                      إنشاء حساب
                    </Link>
                  </div>
                )}
              </div>
            </div>

            {/* Mobile */}
            <div className="flex h-full w-full items-center justify-between lg:hidden">
              <button type="button" onClick={toggleNightMode} className="group relative p-1 focus:outline-none" aria-label={isNightMode ? 'الوضع النهاري' : 'الوضع الليلي'}>
                <div
                  className={`relative h-7 w-14 rounded-full shadow-xl transition-all duration-700 ease-out group-hover:scale-105 group-hover:shadow-lg ${
                    isNightMode
                      ? 'bg-gradient-to-br from-orange-600 via-orange-600 to-orange-700 shadow-orange-600/40'
                      : 'bg-gradient-to-br from-orange-100 via-orange-200 to-orange-300 shadow-orange-600/20'
                  }`}
                >
                  {isNightMode && (
                    <div className="absolute inset-0 overflow-hidden rounded-full">
                      <div className="absolute left-1.5 top-0.5 h-0.5 w-0.5 animate-pulse rounded-full bg-yellow-200" />
                      <div className="absolute right-2 top-1.5 h-0.5 w-0.5 animate-ping rounded-full bg-white" />
                      <div className="absolute bottom-1 left-2 h-0.5 w-0.5 animate-pulse rounded-full bg-yellow-100" style={{ animationDelay: '1s' }} />
                    </div>
                  )}
                  <div
                    className={`absolute top-0.5 h-6 w-6 rounded-full shadow-lg transition-all duration-700 ease-out ${
                      isNightMode ? 'left-7' : 'left-0.5'
                    } ${
                      isNightMode
                        ? 'bg-gradient-to-br from-orange-600 via-orange-600 to-orange-700 shadow-orange-600/50'
                        : 'bg-gradient-to-br from-orange-600 via-orange-600 to-orange-600 shadow-orange-600/30'
                    } group-hover:shadow-lg`}
                  >
                    <div
                      className={`absolute inset-0 rounded-full transition-all duration-700 ${
                        isNightMode ? 'ring-1 ring-orange-600/50 ring-offset-0.5 ring-offset-transparent' : 'ring-1 ring-orange-600/50'
                      }`}
                    />
                    <div className="relative z-10 flex h-full w-full items-center justify-center">
                      {isNightMode ? (
                        <Sun className="h-3 w-3 text-white drop-shadow-sm transition-all duration-500 group-hover:rotate-180 group-hover:scale-110" />
                      ) : (
                        <Moon className="h-3 w-3 text-white drop-shadow-sm transition-all duration-500 group-hover:rotate-12 group-hover:scale-110" />
                      )}
                    </div>
                    <div
                      className={`absolute inset-0.5 rounded-full blur-sm transition-all duration-700 ${
                        isNightMode ? 'bg-gradient-to-br from-orange-600/30 to-orange-700/30' : 'bg-gradient-to-br from-orange-600/50 to-orange-600/30'
                      }`}
                    />
                  </div>
                  <div className="absolute inset-y-0 left-1.5 flex items-center">
                    <div className={`h-0.5 w-0.5 rounded-full transition-all duration-500 ${!isNightMode ? 'bg-orange-600' : 'bg-transparent'}`} />
                  </div>
                  <div className="absolute inset-y-0 right-1.5 flex items-center">
                    <div className={`h-0.5 w-0.5 rounded-full transition-all duration-500 ${isNightMode ? 'bg-orange-600' : 'bg-transparent'}`} />
                  </div>
                  <div
                    className={`absolute inset-0 rounded-full transition-all duration-700 ${
                      isNightMode
                        ? 'shadow-[0_0_15px_rgba(249,115,22,0.3)] group-hover:shadow-[0_0_20px_rgba(249,115,22,0.4)]'
                        : 'shadow-[0_0_8px_rgba(249,115,22,0.2)] group-hover:shadow-[0_0_12px_rgba(249,115,22,0.3)]'
                    }`}
                  />
                </div>
              </button>

              <Link to="/" className="group flex min-w-0 max-w-[min(100%,11rem)] items-center justify-center">
                <div className="relative h-11 w-[7.5rem] overflow-hidden rounded-2xl border-2 border-orange-600 bg-gradient-to-br from-white to-orange-50 shadow-lg sm:h-12 sm:w-[8.5rem]">
                  <div className="flex h-full w-full items-center justify-center p-1.5 sm:p-2">
                    <img
                      src={logoImage}
                      alt="منصة مستر وليد المصري"
                      className="max-h-full max-w-full object-contain object-center select-none"
                    />
                  </div>
                </div>
              </Link>

              <button
                type="button"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="p-2 transition-all hover:scale-110"
                aria-expanded={isMobileMenuOpen}
                aria-label={isMobileMenuOpen ? 'إغلاق القائمة' : 'فتح القائمة'}
              >
                {!isMobileMenuOpen ? <Menu className="h-6 w-6 text-orange-600" /> : <X className="h-6 w-6 text-orange-600" />}
              </button>
            </div>
          </div>

          {isMobileMenuOpen && (
            <div className="absolute left-0 right-0 top-full z-50 mx-1 mt-2 rounded-3xl border border-orange-200/50 bg-white/95 shadow-2xl backdrop-blur-2xl lg:hidden">
              <div className="space-y-2 p-3">
                <Link
                  to="/"
                  className={`flex w-full items-center justify-center rounded-xl px-4 py-3 transition-all duration-300 ${
                    isCurrentPath('/')
                      ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-lg shadow-orange-500/25'
                      : 'text-gray-700 hover:bg-gradient-to-r hover:from-orange-50 hover:to-orange-100 hover:text-orange-700'
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  الرئيسية
                </Link>
                <button
                  type="button"
                  onClick={() => {
                    scrollToCourses();
                    setIsMobileMenuOpen(false);
                  }}
                  className="flex w-full items-center justify-center rounded-xl px-4 py-3 text-gray-700 transition-all duration-300 hover:bg-gradient-to-r hover:from-orange-50 hover:to-orange-100 hover:text-orange-700"
                >
                  الباقات
                </button>
                <Link
                  to="/parent-monitoring"
                  className={`flex w-full items-center justify-center rounded-xl px-4 py-3 transition-all duration-300 ${
                    isCurrentPath('/parent-monitoring')
                      ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-lg shadow-orange-500/25'
                      : 'text-gray-700 hover:bg-gradient-to-r hover:from-orange-50 hover:to-orange-100 hover:text-orange-700'
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  متابعة ولي الأمر
                </Link>
                {isAuthenticated && (
                  <>
                    <Link
                      to="/my-lectures"
                      className={`flex w-full items-center justify-center rounded-xl px-4 py-3 transition-all duration-300 ${
                        isCurrentPath('/my-lectures')
                          ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-lg shadow-orange-500/25'
                          : 'text-gray-700 hover:bg-gradient-to-r hover:from-orange-50 hover:to-orange-100 hover:text-orange-700'
                      }`}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      محاضراتي
                    </Link>
                    <Link
                      to="/enrolled-courses"
                      className={`flex w-full items-center justify-center rounded-xl px-4 py-3 transition-all duration-300 ${
                        isCurrentPath('/enrolled-courses')
                          ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-lg shadow-orange-500/25'
                          : 'text-gray-700 hover:bg-gradient-to-r hover:from-orange-50 hover:to-orange-100 hover:text-orange-700'
                      }`}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      باقاتي
                    </Link>
                    <Link
                      to="/statistics"
                      className={`flex w-full items-center justify-center rounded-xl px-4 py-3 transition-all duration-300 ${
                        isCurrentPath('/statistics')
                          ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow-lg shadow-orange-500/25'
                          : 'text-gray-700 hover:bg-gradient-to-r hover:from-orange-50 hover:to-orange-100 hover:text-orange-700'
                      }`}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      احصائياتي
                    </Link>
                  </>
                )}
                <div className="border-t border-orange-200/50 pt-4">
                  {isAuthenticated ? (
                    <button
                      type="button"
                      onClick={() => {
                        logout();
                        setIsMobileMenuOpen(false);
                      }}
                      className="flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-red-400 to-red-500 px-4 py-3 text-white shadow-lg"
                    >
                      <LogOut className="h-5 w-5" />
                      تسجيل الخروج
                    </button>
                  ) : (
                    <div className="space-y-3">
                      <Link
                        to="/login"
                        className="block w-full rounded-xl border border-orange-200 px-4 py-3 text-center text-gray-700 hover:bg-orange-50 hover:text-orange-700"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        تسجيل الدخول
                      </Link>
                      <Link
                        to="/register"
                        className="block w-full rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 px-4 py-3 text-center text-white shadow-lg"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        <Sparkles className="mr-1 inline-block h-4 w-4" />
                        إنشاء حساب
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </nav>
    </>
  );
};

export default Navbar;
